import { useEffect, useRef, useState } from "react";
import { X, GripHorizontal, Loader2 } from "lucide-react";
import { OrderButton, SideSwitch, useTween } from "./motion";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useMarket } from "../lib/store";
import { useGtt } from "../lib/gtt";
import { useSubscribe, useTick, useMeta } from "../lib/socket";
import { fmt, inr, pct, tone, isDerivative } from "../lib/format";
import { Price } from "./Price";

const Seg = ({ options, value, onChange, testid, side }) => (
  <div className="flex gap-1 flex-wrap">
    {options.map(([v, label, hint]) => (
      <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)} title={hint}
        className={`h-7 px-2.5 rounded text-[11px] font-medium border transition-colors duration-150 ${value === v
          ? side === "SELL" ? "border-down text-down bg-down/10" : "border-up text-up bg-up/10"
          : "border-border text-muted-foreground hover:text-foreground"}`}>
        {label}
      </button>
    ))}
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow block mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full h-9 px-2.5 rounded-md border border-input bg-background num text-[13px] outline-none focus:border-primary disabled:opacity-40";

function useOrderForm(order, inst, tick) {
  const der = isDerivative(inst);
  const m = order.modify;
  const [f, setF] = useState(() => ({
    side: m?.side || order.side || "BUY",
    product: m?.product || order.product || (der ? "NRML" : "CNC"),
    orderType: m?.order_type || order.orderType || "LIMIT",
    variety: m ? (m.amo_pending ? "AMO" : m.variety) : "REGULAR",
    validity: m?.validity || "DAY",
    lots: m ? m.quantity / (m.lot_size || 1) : order.qty ? order.qty / (inst?.lot_size || 1) : 1,
    price: m?.price || order.price || "",
    trigger: m?.trigger_price || "",
    coTrigger: "",
    legs: 2,
  }));
  useEffect(() => {
    if (!f.price && tick?.ltp) setF((x) => ({ ...x, price: tick.ltp }));
  }, [tick?.ltp, f.price]);
  return [f, (patch) => setF((x) => ({ ...x, ...patch }))];
}

function Footer({ preview, busy, f, onSubmit, modify, btn, filledAt }) {
  const total = preview?.ok ? preview.required_margin + (preview.charges?.total || 0) : null;
  const tw = useTween(total, 450);
  return (
    <div className="border-t border-border px-4 py-3 bg-muted/30 space-y-2">
      {preview?.error && <div data-testid="order-validation-error" className="text-[11px] text-down">{preview.error}</div>}
      <div className="flex items-end justify-between gap-3">
        <div className="text-[11px] space-y-0.5">
          <div><span className="text-muted-foreground">Required </span><span className="num font-semibold" data-testid="order-required-margin">{total == null ? "—" : inr(tw)}</span></div>
          <div><span className="text-muted-foreground">Available </span><span className="num" data-testid="order-available-margin">{inr(preview?.available_margin)}</span></div>
          <div title={preview?.charges ? Object.entries(preview.charges).map(([k, v]) => `${k}: ${v}`).join("\n") : ""}>
            <span className="text-muted-foreground">Charges </span><span className="num" data-testid="order-charges">{inr(preview?.charges?.total)}</span>
          </div>
        </div>
        <OrderButton testid="order-submit-button" state={btn} side={f.side} onClick={onSubmit} filledText={`Filled at ₹${filledAt}`}
          label={modify ? "Modify" : f.side === "BUY" ? "Buy" : "Sell"} />
      </div>
    </div>
  );
}

function OrderBody({ order, inst, tick, close }) {
  const [f, set] = useOrderForm(order, inst, tick);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [btn, setBtn] = useState("idle");
  const [filledAt, setFilledAt] = useState(null);
  useEffect(() => { if (order.side && order.side !== f.side) set({ side: order.side }); }, [order.side]); // eslint-disable-line react-hooks/exhaustive-deps
  const der = isDerivative(inst);
  const lot = inst?.lot_size || 1;
  const qty = Math.max(0, Math.round(Number(f.lots) || 0)) * lot;
  const modify = order.modify;
  const needPrice = ["LIMIT", "SL"].includes(f.orderType);
  const needTrig = ["SL", "SL-M"].includes(f.orderType);
  const payload = {
    instrument_key: inst.instrument_key, side: f.side, product: f.product, order_type: f.orderType,
    variety: f.variety, validity: f.validity, quantity: qty,
    price: needPrice ? Number(f.price) || 0 : 0, trigger_price: needTrig ? Number(f.trigger) || 0 : 0,
    co_trigger: f.variety === "CO" ? Number(f.coTrigger) || 0 : 0, iceberg_legs: Number(f.legs) || 2,
  };
  const sig = JSON.stringify(payload);
  useEffect(() => {
    if (!qty) return undefined;
    const t = setTimeout(() => api.post("/orders/preview", JSON.parse(sig)).then((r) => setPreview(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [sig, qty]);

  const reject = (reason, withToast = true) => {
    setBtn("rejected");
    setTimeout(() => { setBtn("idle"); if (withToast) toast.error("Simulated order rejected", { description: reason }); }, 1200);
  };
  const submit = async () => {
    if (btn === "loading" || btn === "filled") return;
    setBusy(true);
    setBtn("loading");
    try {
      if (modify) {
        await api.put(`/orders/${modify.id}`, { quantity: qty, price: payload.price, trigger_price: payload.trigger_price, order_type: f.orderType });
        toast.success(`Simulated order modified · ${inst.trading_symbol}`);
      } else {
        const { data } = await api.post("/orders", payload);
        const o = data[0];
        if (o && ["OPEN", "TRIGGER_PENDING"].includes(o.status) && !o.filled_qty) toast.message(`Simulated order placed · ${o.side} ${o.quantity} ${o.symbol}`, { description: o.amo_pending ? "AMO — will be sent at market open" : o.status.replace("_", " ") });
        if (o?.status === "REJECTED") { setPreview((p) => ({ ...(p || {}), error: o.reason })); setBusy(false); reject(o.reason, false); return; }
        if (o?.filled_qty) {
          setFilledAt(o.avg_price);
          setBtn("filled");
          setBusy(false);
          setTimeout(close, 1100);
          return;
        }
      }
      close();
    } catch (e) {
      reject(errMsg(e));
    }
    setBusy(false);
  };

  const products = der ? [["NRML", "NRML", "Overnight"], ["MIS", "MIS", "Intraday"]] : [["CNC", "CNC", "Delivery"], ["MIS", "MIS", "Intraday"]];
  const toGtt = (type) => { close(); useGtt.getState().openGtt({ key: inst.instrument_key, side: f.side, pre: { product: f.product, lots: Math.max(1, Math.round(Number(f.lots) || 1)), type } }); };
  const bestBid = tick?.depth?.buy?.[0]?.[0];
  const bestAsk = tick?.depth?.sell?.[0]?.[0];
  return (
    <>
      <div className="px-4 pt-3 pb-2 flex flex-wrap gap-1 border-b border-border">
        {[["REGULAR", "Regular"], ["AMO", "AMO"], ["CO", "Cover"], ["ICEBERG", "Iceberg"]].map(([v, l]) => (
          <button key={v} disabled={!!modify} data-testid={`order-variety-${v}`} onClick={() => set({ variety: v, ...(v === "CO" ? { product: "MIS", orderType: f.orderType === "LIMIT" ? "LIMIT" : "MARKET" } : {}) })}
            className={`h-7 px-3 text-[11px] font-medium rounded-full transition-colors duration-150 disabled:opacity-40 ${f.variety === v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-accent"}`}>{l}</button>
        ))}
        {!modify && <button data-testid="order-variety-GTT" onClick={() => toGtt()} title="Good Till Triggered order" className="h-7 px-3 text-[11px] font-medium rounded-full text-muted-foreground hover:bg-accent transition-colors duration-150">GTT</button>}
      </div>
      {order.exit && (
        <button data-testid="order-exit-sltp" onClick={() => toGtt("oco")}
          className="mx-4 mt-3 w-[calc(100%-2rem)] h-9 rounded-md border border-primary/60 text-primary text-[12px] font-semibold hover:bg-primary/10 transition-colors duration-150">
          Set Stop-loss &amp; Target instead
        </button>
      )}
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-center justify-between text-[11px] num">
          <span>LTP <span className={tone(tick?.ch)}><Price value={tick?.ltp} prev={tick?.prevLtp} testid="order-ltp" /></span> <span className={tone(tick?.ch)}>{pct(tick?.chp)}</span></span>
          <span><span className="text-up" data-testid="order-best-bid">{fmt(bestBid)}</span> <span className="text-muted-foreground">/</span> <span className="text-down" data-testid="order-best-ask">{fmt(bestAsk)}</span></span>
        </div>
        <Seg testid="order-product" side={f.side} value={f.product} onChange={(v) => set({ product: v })} options={f.variety === "CO" ? [["MIS", "MIS"]] : products} />
        <div className="grid grid-cols-3 gap-2">
          <Field label={der ? `Lots (×${lot})` : "Qty"}>
            <input data-testid="order-quantity-input" type="number" min="1" value={f.lots} onChange={(e) => set({ lots: e.target.value })} className={inputCls} />
            {der && <span className="text-[10px] text-muted-foreground num">Qty {qty}</span>}
          </Field>
          <Field label="Price">
            <input data-testid="order-price-input" type="number" step={inst.tick_size} disabled={!needPrice} value={needPrice ? f.price : ""} placeholder={needPrice ? "" : "Market"}
              onChange={(e) => set({ price: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Trigger">
            <input data-testid="order-trigger-input" type="number" step={inst.tick_size} disabled={!needTrig} value={f.trigger} onChange={(e) => set({ trigger: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <Seg testid="order-type" side={f.side} value={f.orderType} onChange={(v) => set({ orderType: v })}
          options={(f.variety === "CO" ? ["MARKET", "LIMIT"] : ["MARKET", "LIMIT", "SL", "SL-M"]).map((x) => [x, x])} />
        {f.variety === "CO" && (
          <Field label="Stop-loss trigger (mandatory)">
            <input data-testid="order-co-trigger-input" type="number" step={inst.tick_size} value={f.coTrigger} onChange={(e) => set({ coTrigger: e.target.value })} className={inputCls} />
          </Field>
        )}
        {f.variety === "ICEBERG" && (
          <Field label="Number of legs (2–10)">
            <input data-testid="order-iceberg-legs-input" type="number" min="2" max="10" value={f.legs} onChange={(e) => set({ legs: e.target.value })} className={inputCls} />
          </Field>
        )}
        <div className="flex items-center justify-between">
          <Seg testid="order-validity" side={f.side} value={f.validity} onChange={(v) => set({ validity: v })} options={[["DAY", "Day"], ["IOC", "IOC"]]} />
          <span className="text-[10px] text-muted-foreground num">Tick {inst.tick_size}{lot > 1 ? ` · Lot ${lot}` : ""}</span>
        </div>
      </div>
      <Footer preview={preview} busy={busy} f={f} onSubmit={submit} modify={modify} btn={btn} filledAt={filledAt} />
    </>
  );
}

export function OrderWindow() {
  const { order, closeOrder, openOrder } = useMarket();
  const key = order?.modify?.instrument_key || order?.key;
  useSubscribe(key ? [key] : []);
  const inst = useMeta(key);
  const tick = useTick(key);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  useEffect(() => { setPos({ x: 0, y: 0 }); }, [key]);
  if (!order || !key) return null;
  const side = order.modify?.side || order.side;
  const onDown = (e) => {
    drag.current = { sx: e.clientX - pos.x, sy: e.clientY - pos.y };
    const mv = (ev) => setPos({ x: ev.clientX - drag.current.sx, y: ev.clientY - drag.current.sy });
    const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  };
  return (
    <div data-testid="order-window-modal" style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
      className="fixed z-[60] bottom-16 md:bottom-auto md:top-20 left-2 right-2 md:left-auto md:right-6 md:w-[420px] rounded-lg border border-border bg-card shadow-[0_24px_80px_-12px_rgba(0,0,0,0.6)] rise overflow-hidden">
      <div onMouseDown={onDown} className={`px-4 py-2.5 cursor-move select-none text-white ${side === "BUY" ? "bg-up" : "bg-down"}`}>
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-semibold text-[13px]"><GripHorizontal className="h-3.5 w-3.5 opacity-60" />
              <span data-testid="order-window-title">{order.modify ? "Modify · " : ""}{side === "BUY" ? "Buy" : "Sell"} {inst?.trading_symbol || "…"}</span></div>
            <div className="text-[10.5px] opacity-85 num">{inst?.exchange} · ₹{fmt(tick?.ltp)}</div>
            <span data-testid="order-window-sim-label" className="mt-0.5 inline-block whitespace-nowrap rounded bg-black/25 px-1 text-[9.5px] font-bold uppercase tracking-[0.12em]">Simulated order</span>
          </div>
          <div className="flex items-center gap-2" onMouseDown={(e) => e.stopPropagation()}>
            {!order.modify && (<>
              <SideSwitch side={side} onChange={(s) => openOrder({ ...order, side: s })} />
              <button data-testid="order-side-toggle" onClick={() => openOrder({ ...order, side: side === "BUY" ? "SELL" : "BUY" })} className="sr-only">{side === "BUY" ? "SWITCH TO SELL" : "SWITCH TO BUY"}</button>
            </>)}
            <button data-testid="order-window-close" onClick={closeOrder} className="h-6 w-6 grid place-items-center rounded hover:bg-white/20"><X className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {inst ? <OrderBody key={`${key}-${order.modify?.id || ""}`} order={{ ...order, side }} inst={inst} tick={tick} close={closeOrder} />
        : <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>}
    </div>
  );
}
